import React, { useCallback, useEffect, useMemo, useState } from "react";
import NavBar from "../components/NavBar";
import GenderBadge from "../components/GenderBadge";
import { apiFetch } from "../api";
import { useLoading } from "../contexts/LoadingContext";
import { RefreshIcon, MailIcon, SaveIcon, CancelIcon } from "../components/icons";
import { formatDate } from "../utils/dateUtils";

// Bureau-side dashboard for incoming interest requests. Bureau approves or
// rejects each request; approval makes the candidate's contact visible to
// the seeker via /my-interests.
const STATUSES = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "introduced", label: "Introduced" },
  { value: "rejected", label: "Rejected" },
  { value: "all", label: "All" },
];

const statusPill = (status) => {
  const map = {
    pending: "bg-yellow-100 text-yellow-800",
    approved: "bg-green-100 text-green-800",
    introduced: "bg-emerald-100 text-emerald-800",
    rejected: "bg-red-100 text-red-800",
  };
  return (
    <span
      className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${
        map[status] || "bg-gray-100 text-gray-800"
      }`}
    >
      {status}
    </span>
  );
};

const BureauInterests = () => {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // Per-row inline response notes keyed by request id.
  const [responseDrafts, setResponseDrafts] = useState({});
  // Per-row pending action ids to disable the buttons while in flight.
  const [busyIds, setBusyIds] = useState(new Set());
  const { setLoading: setGlobalLoading, setGlobalLoadingMessage } = useLoading();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      setGlobalLoadingMessage("Loading interest requests…");
      setGlobalLoading(true);
      const res = await apiFetch("/interests");
      setItems(res?.data || []);
      // Clear the unread badge once the page is open.
      apiFetch("/interests/mark-seen", { method: "POST" }).catch(() => {
        /* non-critical */
      });
      // Tell the sidebar to drop the Interest Requests badge instantly
      // (instead of waiting up to 60s for the next poll).
      window.dispatchEvent(new CustomEvent("interests:seen"));
    } catch (err) {
      console.error("Failed to load interests:", err);
      setError(err.message || "Failed to load interest requests");
    } finally {
      setLoading(false);
      setGlobalLoading(false);
      setGlobalLoadingMessage("");
    }
  }, [setGlobalLoading, setGlobalLoadingMessage]);

  useEffect(() => {
    load();
  }, [load]);

  const counts = useMemo(() => {
    const c = {
      pending: 0,
      approved: 0,
      introduced: 0,
      rejected: 0,
      all: items.length,
    };
    items.forEach((it) => {
      if (c[it.status] !== undefined) c[it.status] += 1;
    });
    return c;
  }, [items]);

  const filtered =
    statusFilter === "all"
      ? items
      : items.filter((it) => it.status === statusFilter);

  // action is "approve" | "reject" | "introduced". Final statuses are
  // "approved" | "rejected" | "introduced" respectively.
  const ACTION_TO_STATUS = {
    approve: "approved",
    reject: "rejected",
    introduced: "introduced",
  };
  const decide = async (id, action) => {
    setBusyIds((s) => new Set(s).add(id));
    try {
      await apiFetch(`/interests/${id}/${action}`, {
        method: "PATCH",
        body: JSON.stringify({
          bureauResponse: responseDrafts[id] || undefined,
        }),
      });
      setItems((prev) =>
        prev.map((it) =>
          it._id === id
            ? {
                ...it,
                status: ACTION_TO_STATUS[action] || it.status,
                bureauResponse: responseDrafts[id] || it.bureauResponse,
                respondedAt: new Date().toISOString(),
              }
            : it
        )
      );
      setResponseDrafts((d) => {
        const next = { ...d };
        delete next[id];
        return next;
      });
    } catch (err) {
      setError(err.message || `Failed to ${action} request`);
    } finally {
      setBusyIds((s) => {
        const next = new Set(s);
        next.delete(id);
        return next;
      });
    }
  };

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <NavBar />

      <div className="sticky top-16 z-20 bg-[var(--color-background)] border-b border-[var(--color-border)]">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <span className="w-9 h-9 shrink-0 rounded-lg bg-[var(--color-primary-soft)] text-[var(--color-primary)] flex items-center justify-center">
              <MailIcon className="w-5 h-5" />
            </span>
            <div className="min-w-0">
              <h1 className="text-base sm:text-lg font-semibold text-[var(--color-text-strong)] truncate">
                Interest Requests
              </h1>
              <p className="text-xs text-[var(--color-text-muted)]">
                Seekers asking for contact details of your profiles
              </p>
            </div>
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] hover:bg-[var(--color-surface-hover)] disabled:opacity-60 px-3 py-1.5 text-sm font-medium transition-colors"
          >
            <RefreshIcon className="w-4 h-4" />
            {loading ? "Loading…" : "Refresh"}
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          {STATUSES.map((s) => (
            <button
              key={s.value}
              onClick={() => setStatusFilter(s.value)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === s.value
                  ? "bg-[var(--color-primary)] text-[var(--color-on-primary)]"
                  : "border border-[var(--color-border)] text-[var(--color-text)] hover:bg-[var(--color-surface-hover)]"
              }`}
            >
              {s.label}
              <span className="ml-1.5 text-xs opacity-80">
                ({counts[s.value] ?? 0})
              </span>
            </button>
          ))}
        </div>

        {error && (
          <div className="rounded-lg border border-[var(--color-danger-border)] bg-[var(--color-danger-bg)] px-3 py-2 text-sm text-[var(--color-danger-text)]">
            {error}
          </div>
        )}

        {!loading && filtered.length === 0 && !error && (
          <div className="text-center py-12 text-sm text-[var(--color-text-muted)]">
            No requests in this view.
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filtered.map((it) => {
            const seeker = it.publicUser || {};
            const p = it.profile || {};
            const busy = busyIds.has(it._id);
            const isPending = it.status === "pending";

            return (
              <div
                key={it._id}
                className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-4 flex flex-col gap-3"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
                      Request {formatDate(it.createdAt)}
                    </div>
                    <div className="text-sm font-semibold text-[var(--color-text-strong)] flex items-center gap-2">
                      <span className="truncate">
                        {seeker.fullName || "Unknown seeker"}
                      </span>
                      <GenderBadge gender={seeker.gender} />
                    </div>
                    <div className="text-xs text-[var(--color-text-muted)] truncate">
                      {seeker.email || "—"}
                      {seeker.contactNumber && (
                        <>
                          {" · "}
                          <a
                            href={`tel:${seeker.contactNumber}`}
                            className="hover:underline"
                          >
                            {seeker.contactNumber}
                          </a>
                        </>
                      )}
                    </div>
                  </div>
                  {statusPill(it.status)}
                </div>

                <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-muted)] p-2">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)] mb-0.5">
                    Interested in
                  </div>
                  <div className="text-sm font-semibold text-[var(--color-text-strong)] flex items-center gap-2">
                    <span className="truncate">
                      {p.fullName || "—"} (ID {p.referenceId || "—"})
                    </span>
                    <GenderBadge gender={p.gender} />
                  </div>
                  <div className="text-xs text-[var(--color-text-muted)] truncate">
                    {p.age ? `Age ${p.age}` : ""}
                    {p.city ? ` · ${p.city}` : ""}
                    {p.profession ? ` · ${p.profession}` : ""}
                  </div>
                  {/* Candidate contact — revealed once the bureau has
                      approved (or already introduced) the match, so the
                      bureau sees both phones in one place and can act on
                      the introduction directly from this card. */}
                  {!isPending && p.contactNumber && (
                    <div className="mt-1 text-xs text-[var(--color-text-muted)] truncate">
                      <a
                        href={`tel:${p.contactNumber}`}
                        className="hover:underline"
                      >
                        Call candidate: {p.contactNumber}
                      </a>
                      {p.email ? ` · ${p.email}` : ""}
                    </div>
                  )}
                </div>

                {it.message && (
                  <div className="text-xs">
                    <div className="font-semibold text-[10px] uppercase tracking-wider text-[var(--color-text-muted)] mb-0.5">
                      Message from seeker
                    </div>
                    <div className="whitespace-pre-wrap text-[var(--color-text)]">
                      {it.message}
                    </div>
                  </div>
                )}

                {!isPending && it.bureauResponse && (
                  <div className="text-xs">
                    <div className="font-semibold text-[10px] uppercase tracking-wider text-[var(--color-text-muted)] mb-0.5">
                      Your response
                    </div>
                    <div className="whitespace-pre-wrap text-[var(--color-text)]">
                      {it.bureauResponse}
                    </div>
                  </div>
                )}

                {isPending && (
                  <>
                    <textarea
                      rows={2}
                      value={responseDrafts[it._id] || ""}
                      onChange={(e) =>
                        setResponseDrafts((d) => ({
                          ...d,
                          [it._id]: e.target.value,
                        }))
                      }
                      placeholder="Optional response note (e.g. share when to expect a call)"
                      className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-xs"
                    />
                    <div className="flex gap-2 flex-wrap">
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => decide(it._id, "approve")}
                        className="inline-flex items-center gap-2 rounded-lg bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-[var(--color-on-primary)] disabled:opacity-60 px-3 py-1.5 text-sm font-semibold transition-colors"
                      >
                        <SaveIcon className="w-4 h-4" />
                        {p.contactNumber
                          ? "Approve & share contact"
                          : "Approve & introduce manually"}
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => decide(it._id, "reject")}
                        className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] text-[var(--color-text)] hover:bg-[var(--color-surface-hover)] disabled:opacity-60 px-3 py-1.5 text-sm font-medium transition-colors"
                      >
                        <CancelIcon className="w-4 h-4" />
                        Reject
                      </button>
                    </div>
                  </>
                )}

                {/* Approved-without-stored-phone needs an explicit follow-up:
                    bureau has to call both sides, then mark introduction made. */}
                {it.status === "approved" && !p.contactNumber && (
                  <div className="rounded-lg border-2 border-[var(--color-primary)] bg-[var(--color-primary-soft)] p-2 space-y-2">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--color-primary)]">
                      Introduction needed
                    </div>
                    <p className="text-xs text-[var(--color-text)]">
                      The seeker is waiting. Call both parties using the
                      number below + your own records, then mark this done.
                    </p>
                    {seeker.contactNumber && (
                      <a
                        href={`tel:${seeker.contactNumber}`}
                        className="block font-mono font-semibold text-sm text-[var(--color-primary)] hover:underline"
                      >
                        Call seeker: {seeker.contactNumber}
                      </a>
                    )}
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => decide(it._id, "introduced")}
                      className="inline-flex items-center gap-2 rounded-lg bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-[var(--color-on-primary)] disabled:opacity-60 px-3 py-1.5 text-sm font-semibold transition-colors"
                    >
                      <SaveIcon className="w-4 h-4" />
                      Mark introduction made
                    </button>
                  </div>
                )}

                {it.respondedAt && (
                  <div className="text-[10px] text-[var(--color-text-muted)] mt-auto">
                    {it.status === "introduced"
                      ? `Introduced ${formatDate(it.respondedAt)}`
                      : `Decided ${formatDate(it.respondedAt)}`}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default BureauInterests;
